import type { FingerPrickPastReading } from "./measurePageMappers";
import { mealTimingLabelFromKey } from "./measurePageMappers";

const STORAGE_KEY = "glucera_finger_prick_log_history";

export type GlucoseLogReadingType = "finger_prick" | "cgm" | "fasting" | "hba1c";

const READING_TYPE_LABELS: Record<GlucoseLogReadingType, string> = {
  finger_prick: "Finger prick",
  cgm: "CGM",
  fasting: "Fasting glucose",
  hba1c: "HbA1c",
};

export interface FingerPrickLogEntry {
  mgDl: number;
  date: string;
  time: string;
  /** Which glucose source was logged — defaults to finger prick for older entries. */
  readingType?: GlucoseLogReadingType;
  /** Pre-meal or post-meal context when logged from Measure page. */
  mealTiming?: "pre-meal" | "post-meal";
}

const MONTHS_UPPER = [
  "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
  "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
] as const;

function formatTimeLabel(timeStr: string): string {
  const [hhRaw, mmRaw] = timeStr.split(":");
  let h = Number.parseInt(hhRaw ?? "0", 10);
  const m = mmRaw ?? "00";
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${ampm}`;
}

function formatHistoryDate(dateStr: string): string {
  const [yRaw, moRaw, dRaw] = dateStr.split("-");
  const y = Number(yRaw);
  const mo = Number(moRaw);
  const d = Number(dRaw);
  if (!y || !mo || !d) return dateStr;
  const mon = MONTHS_UPPER[mo - 1] ?? "???";
  return `${mon} ${String(d).padStart(2, "0")}, ${y}`;
}

function formatReadingValue(mgDl: number, readingType: GlucoseLogReadingType = "finger_prick"): string {
  if (readingType === "hba1c") return `${mgDl}%`;
  return `${mgDl} mg/dL`;
}

function entrySortKey(date: string, time: string): number {
  const ms = new Date(`${date}T${time || "00:00"}:00`).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

export function loadFingerPrickLogHistory(): FingerPrickLogEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as FingerPrickLogEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function appendFingerPrickLogHistory(entry: FingerPrickLogEntry): void {
  const list = loadFingerPrickLogHistory();
  list.push(entry);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

export function fingerLogEntriesToPastReadings(
  entries: FingerPrickLogEntry[],
): FingerPrickPastReading[] {
  return entries.map((entry) => {
    const readingType = entry.readingType ?? "finger_prick";
    return {
      typeLabel: READING_TYPE_LABELS[readingType],
      value: formatReadingValue(entry.mgDl, readingType),
      meta: `${formatHistoryDate(entry.date)} · ${formatTimeLabel(entry.time)}`,
      mealTimingLabel: mealTimingLabelFromKey(entry.mealTiming),
      sortKey: entrySortKey(entry.date, entry.time),
    };
  });
}

function readingRowKey(r: FingerPrickPastReading): string {
  return `${r.typeLabel}|${r.mealTimingLabel ?? ""}|${r.value}|${r.meta}`.toLowerCase();
}

/** Loose key — only value + meta, ignores typeLabel/mealTiming differences between sources. */
function readingValueMetaKey(r: FingerPrickPastReading): string {
  return `${r.value.replace(/\s/g, "")}|${r.meta.replace(/\s/g, "")}`.toLowerCase();
}

/** API list first, then local-only readings not already in the API list.
 *  Deduplication is done on both exact key AND value+meta to prevent
 *  the same physical reading appearing twice when it arrives from both
 *  the measure API (CGM / post_meal) and the glucose-readings snapshot. */
export function mergeFingerPrickPastReadings(
  fromApi: FingerPrickPastReading[],
  fromLocal: FingerPrickPastReading[],
): FingerPrickPastReading[] {
  const apiKeys = new Set(fromApi.map(readingRowKey));
  const apiValueMetaKeys = new Set(fromApi.map(readingValueMetaKey));

  const extraLocal = fromLocal.filter(
    (r) => !apiKeys.has(readingRowKey(r)) && !apiValueMetaKeys.has(readingValueMetaKey(r)),
  );

  return [...fromApi, ...extraLocal];
}
